import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import RenderLimitedText from "go-core/components/RenderLimitedText";
import Tag from "../../../../../../../components/Common/Tag/Tag";
import { UserApi } from "../../../../../../../services/Api/types";
import DropdownList from "./DropdownList";

interface Props {
	data: UserApi[];
	setSelectedSorts: (sorts: string[]) => void;
	selectedSorts: string[];
}

const UserList = ({ data, setSelectedSorts, selectedSorts }: Props) => {
	const { t } = useTranslation("admin");
	const headerColumns = [
		{
			id: "name",
			title: t("lib:common.word.name"),
		},
		{
			id: "tags",
			title: t("common.word.tags"),
		},
		{
			id: "phone_number",
			title: t("lib:common.word.phone_number"),
		},
		{
			id: "email",
			title: t("lib:common.word.email"),
		},
		{
			id: "organizations",
			title: t("lib:common.word.locations"),
		},
		{
			id: "type",
			title: t("lib:common.word.type"),
		},
		{
			id: "intercom",
			title: t("modules.support.field.intercom.title"),
		},
	];

	const drawOrganizations = (item: UserApi) => {
		return item?.organizations?.map((org, index) => {
			const link =
				org.type === "GOACCOUNTS" ? `/support/organizations_with_access/${org.id}` : org?.intercom_url ?? "";
			const type = org.type === "GOACCOUNTS" ? "GoAccounts" : "Intercom";

			return (
				<div key={index} className="d-flex">
					<span className={`badge me-1 ${org.type === "GOACCOUNTS" ? "bg-success" : "bg-primary"}`}>
						{type}
					</span>
					{link ? (
						<Link to={link} key={`organization-${index}`}>
							{org.name}
						</Link>
					) : (
						<span>{org.name}</span>
					)}
				</div>
			);
		});
	};

	const drawTags = (item: UserApi) => {
		return item?.tags?.map((tag, index) => <Tag key={`user-${item.id}-tag-${index}`}>{tag}</Tag>);
	};

	const getDataColumns = (item: UserApi) => [
		<>{item.name}</>,
		<RenderLimitedText key={item.id} minWidth={300}>
			<div className="d-flex flex-wrap w-100">{drawTags(item)}</div>
		</RenderLimitedText>,
		<>{item.phone_number}</>,
		<>{item.email}</>,
		<RenderLimitedText key={item.id} minWidth={300}>
			{drawOrganizations(item)}
		</RenderLimitedText>,
		<>{item.type}</>,
		<a key={item.id} href={item.intercom_url}>
			{item.intercom_url}
		</a>,
	];

	return (
		<div className="my-4">
			<DropdownList
				title={t("common.word.users", { ns: "lib" })}
				headerColumns={headerColumns}
				dataColumns={getDataColumns}
				setSelectedSorts={setSelectedSorts}
				selectedSorts={selectedSorts}
				items={data}
			/>
		</div>
	);
};

export default UserList;
